/**
 * Autor: CI Sistemas de Informática Ltda.
 * Data: 2026-06-09
 * Descrição: Página de gerenciamento de câmeras do sistema de estacionamento.
 *            Inclui listagem, criação, edição, exclusão, ativação/desativação
 *            e configuração visual de ROI (Region of Interest) por câmera.
 *
 * ROI: editor visual que exibe o último snapshot da câmera e permite ao
 *      operador arrastar um retângulo para delimitar a zona de leitura de
 *      placas. Coordenadas são salvas em percentual [0,1] para ser
 *      independente de resolução.
 */
import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiCheck, FiX,
  FiWifi, FiWifiOff, FiCrosshair, FiExternalLink, FiSliders, FiZap
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import RoiEditor from '../components/RoiEditor';
import ParametrosCamera from '../components/ParametrosCamera'; // Onda 136: o perfil ALPR pela plataforma
import '../styles/Cameras.css';

// Onda 17: o editor de zona (retângulo ou polígono) vive em components/RoiEditor.js

// ─── Componente principal: Câmeras ────────────────────────────────────────────
// Onda 141: o formulário vazio, com os campos do semáforo (desligado, sem controladora).
// Onda 179: gate_* = a controladora de liberação de saída (código, relé 1 ou 2, pulso em ms), só na câmera de saída.
const FORM_VAZIO = { name: '', rtsp_url: '', camera_type: 'entrada', camera_ip_address: '', recognition_source: 'motor', frame_interval: 2000, semaforo_enabled: false, semaforo_device: '', gate_device: '', gate_relay: '', gate_pulse_ms: 1000 };

const Cameras = () => {
  const { can, vertical } = useAuth();
  // Onda 141: o Semáforo da Guarita é do clube e do condomínio; o estacionamento não o tem.
  const temSemaforo = vertical !== 'estacionamento';
  const [cameras,    setCameras]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [roiCamera,  setRoiCamera]  = useState(null);  // câmera sendo editada no modal ROI
  const [paramCamera, setParamCamera] = useState(null); // Onda 136: câmera no modal de parâmetros
  const [testandoSemaforo, setTestandoSemaforo] = useState(null); // Onda 141: `${id}:${cor}` em curso
  const [formData,   setFormData]   = useState({ ...FORM_VAZIO });

  const fetchCameras = async () => {
    try {
      const response = await api.get('/api/cameras');
      setCameras(response.data);
    } catch (error) {
      toast.error('Erro ao carregar câmeras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/cameras/${editingId}`, formData);
        toast.success('Câmera atualizada com sucesso');
      } else {
        await api.post('/api/cameras', formData);
        toast.success('Câmera criada com sucesso');
      }
      setFormData({ ...FORM_VAZIO });
      setEditingId(null);
      setShowForm(false);
      fetchCameras();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar câmera');
    }
  };

  const handleEdit = (camera) => {
    setFormData({
      name: camera.name,
      rtsp_url: camera.rtsp_url,
      camera_type: camera.camera_type,
      camera_ip_address: camera.camera_ip_address || '',
      recognition_source: camera.recognition_source || 'motor',   // Onda 195
      frame_interval: camera.frame_interval,
      semaforo_enabled: Number(camera.semaforo_enabled) === 1,
      semaforo_device: camera.semaforo_device || '',
      gate_device: camera.gate_device || '',                       // Onda 179
      gate_relay: camera.gate_relay ? String(camera.gate_relay) : '',
      gate_pulse_ms: Number(camera.gate_pulse_ms) || 1000,
    });
    setEditingId(camera.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta câmera? Esta ação não pode ser desfeita.')) {
      try {
        await api.delete(`/api/cameras/${id}`);
        toast.success('Câmera excluída com sucesso');
        fetchCameras();
      } catch (error) {
        toast.error('Erro ao excluir câmera');
      }
    }
  };

  const handleToggle = async (camera) => {
    const acao = camera.status === 'ativa' ? 'desativar' : 'ativar';
    if (!window.confirm(`Deseja ${acao} a câmera "${camera.name}"?`)) return;

    setTogglingId(camera.id);
    try {
      const response = await api.patch(`/api/cameras/${camera.id}/toggle`);
      const novoStatus = response.data.status;
      toast.success(
        novoStatus === 'ativa'
          ? `✅ Câmera "${camera.name}" ativada — captura iniciada`
          : `⏸ Câmera "${camera.name}" desativada — captura pausada`
      );
      fetchCameras();
    } catch (error) {
      toast.error(error.response?.data?.error || `Erro ao ${acao} câmera`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ ...FORM_VAZIO });
  };

  // Onda 141: o teste do instalador — manda a cor para a controladora da câmera, mesmo com o
  // semáforo desligado; a lâmpada da bancada é a prova.
  const testarSemaforo = async (camera, cor) => {
    setTestandoSemaforo(`${camera.id}:${cor}`);
    try {
      await api.post(`/api/semaforo/testar/${camera.id}`, { cor });
      toast.success(`${{ verde: 'Verde', vermelho: 'Vermelho', ambar: 'Âmbar' }[cor] || cor} enviado para ${camera.semaforo_device}. Confira a lâmpada na bancada.`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'O sinal de teste não saiu', { duration: 8000 });
    } finally {
      setTestandoSemaforo(null);
    }
  };

  // Onda 179: o teste da controladora de saída — "abrir" pulsa o relé da câmera (bipe longo) e
  // espera a confirmação da placa; "negar" só dá os dois bipes do bloqueio.
  const testarLiberacao = async (camera, acao) => {
    setTestandoSemaforo(`${camera.id}:${acao}`);
    try {
      const r = await api.post(`/api/semaforo/testar-saida/${camera.id}`, { acao });
      if (acao === 'abrir') {
        if (r.data.confirmado) toast.success(`Relé ${r.data.rele} de ${camera.gate_device} pulsou ${r.data.ms} ms e a placa confirmou.`);
        else toast.error(`O comando saiu, mas ${camera.gate_device} não confirmou em 2 s: confira se a placa está ligada e na rede.`, { duration: 8000 });
      } else {
        toast.success(`Bloqueio enviado para ${camera.gate_device}: dois bipes curtos na placa.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'O comando de teste não saiu', { duration: 8000 });
    } finally {
      setTestandoSemaforo(null);
    }
  };

  // ─── ROI ───────────────────────────────────────────────────────────────────
  const handleOpenRoi = (camera) => {
    setRoiCamera(camera);
  };

  const handleSaveRoi = async (roi) => {
    try {
      await api.patch(`/api/cameras/${roiCamera.id}/roi`, { roi });
      if (roi) {
        toast.success(`✅ Zona de leitura configurada para "${roiCamera.name}"`);
      } else {
        toast.success(`🔓 Zona de leitura removida — câmera aceita toda a imagem`);
      }
      setRoiCamera(null);
      fetchCameras();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar zona de leitura');
      throw error; // re-throw para o RoiEditor desativar o spinner
    }
  };

  const getStatusInfo = (status) => {
    const map = {
      ativa:   { label: 'Ativa',   cls: 'status-ativa',   dot: '#22c55e' },
      inativa: { label: 'Inativa', cls: 'status-inativa', dot: '#94a3b8' },
      erro:    { label: 'Erro',    cls: 'status-erro',    dot: '#ef4444' }
    };
    return map[status] || map['inativa'];
  };

  // Parsear roi_config de string JSON para objeto (vem como string do banco)
  const parseRoi = (roiConfig) => {
    if (!roiConfig) return null;
    if (typeof roiConfig === 'object') return roiConfig;
    try { return JSON.parse(roiConfig); } catch (_) { return null; }
  };

  if (loading) {
    return <div className="loading">Carregando câmeras...</div>;
  }

  return (
    <div className="cameras-page">
      {/* Onda 136: parâmetros de imagem da câmera (perfil ALPR) */}
      {paramCamera && (
        <ParametrosCamera
          camera={paramCamera}
          onClose={() => setParamCamera(null)}
        />
      )}

      {/* Modal de edição de ROI */}
      {roiCamera && (
        <RoiEditor
          camera={roiCamera}
          onSave={handleSaveRoi}
          onClose={() => setRoiCamera(null)}
        />
      )}

      <div className="page-header">
        <h1>Câmeras</h1>
        {can('cameras_manage') && (
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData({ ...FORM_VAZIO });
            }}
          >
            <FiPlus /> Nova Câmera
          </button>
        )}
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingId ? 'Editar Câmera' : 'Nova Câmera'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: CAM01Entrada"
                required
              />
            </div>
            <div className="form-group">
              <label>URL RTSP</label>
              <input
                type="text"
                value={formData.rtsp_url}
                onChange={(e) => setFormData({ ...formData, rtsp_url: e.target.value })}
                placeholder="rtsp://usuario:senha@ip:porta/stream"
                required
              />
            </div>
            <div className="form-group">
              <label>IP interno da câmera</label>
              <input
                type="text"
                value={formData.camera_ip_address || ''}
                onChange={(e) => setFormData({ ...formData, camera_ip_address: e.target.value })}
                placeholder="Ex: 192.168.15.20"
              />
              <small>Usado em integrações Clube Social. Não substitui a URL RTSP. Na câmera LPR, é o IP que vem no JSON da câmera.</small>
            </div>
            {/* Onda 195: de onde vem a placa desta câmera. */}
            <div className="form-group">
              <label>Reconhecimento</label>
              <select
                value={formData.recognition_source || 'motor'}
                onChange={(e) => setFormData({ ...formData, recognition_source: e.target.value })}
              >
                <option value="motor">Motor AcessoiD (lê o vídeo da câmera)</option>
                <option value="camera_lpr">Câmera LPR Alphadigi (a placa chega pela integração)</option>
              </select>
              {formData.recognition_source === 'camera_lpr' && (
                <small>A própria câmera lê a placa; o serviço do cliente repassa o JSON dela para a integração. A câmera é achada pelo IP interno acima, e o vídeo não vai ao motor.</small>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={formData.camera_type}
                  onChange={(e) => setFormData({ ...formData, camera_type: e.target.value })}
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                </select>
              </div>
              <div className="form-group">
                <label>Intervalo de Captura (ms)</label>
                <input
                  type="number"
                  value={formData.frame_interval}
                  onChange={(e) => setFormData({ ...formData, frame_interval: parseInt(e.target.value) })}
                  min="500"
                  step="500"
                />
              </div>
            </div>
            {/* Onda 141: o Semáforo da Guarita, por câmera (clube e condomínio). */}
            {temSemaforo && (
              <div className="form-row cam-semaforo-form">
                <div className="form-group">
                  <label htmlFor="cam-semaforo-ligado">Semáforo da guarita</label>
                  <label className="cam-semaforo-toggle" htmlFor="cam-semaforo-ligado">
                    <input
                      id="cam-semaforo-ligado"
                      type="checkbox"
                      checked={!!formData.semaforo_enabled}
                      onChange={(e) => setFormData({ ...formData, semaforo_enabled: e.target.checked })}
                    />
                    {formData.semaforo_enabled ? 'Ligado' : 'Desligado'}
                  </label>
                  <small>Ligado: a cada placa lida nesta câmera, a bancada acende verde (associado/morador) ou vermelho.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="cam-semaforo-codigo">Código da controladora</label>
                  <input
                    id="cam-semaforo-codigo"
                    type="text"
                    value={formData.semaforo_device || ''}
                    onChange={(e) => setFormData({ ...formData, semaforo_device: e.target.value.toUpperCase() })}
                    placeholder="Ex: GUARITA-01"
                    pattern="[A-Za-z0-9_-]{1,40}"
                    required={!!formData.semaforo_enabled}
                  />
                  <small>O mesmo CODIGO_DISPOSITIVO do config.h gravado na placa. O broker fica em Configurações → Integração.</small>
                </div>
              </div>
            )}
            {/* Onda 179: a controladora de liberação de saída, só na câmera de SAÍDA (toda vertical: o G2 e o
                All Suítes cadastram a deles quando chegar a hora). Quem abre é "Abrir cancela e portão
                automaticamente", em Configurações → Integração → Liberação da saída. */}
            {formData.camera_type === 'saida' && (
              <div className="form-row cam-semaforo-form cam-liberacao-form">
                <div className="form-group">
                  <label htmlFor="cam-gate-codigo">Liberação automática de saída — controladora</label>
                  <input
                    id="cam-gate-codigo"
                    type="text"
                    value={formData.gate_device || ''}
                    onChange={(e) => setFormData({ ...formData, gate_device: e.target.value.toUpperCase() })}
                    placeholder="Ex: SAIDA-01"
                    pattern="[A-Za-z0-9_-]{1,40}"
                  />
                  <small>O CODIGO_DISPOSITIVO da controladora de saída (Keyestudio com 2 relés). Vazio: esta câmera nunca abre.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="cam-gate-rele">Relé</label>
                  <select id="cam-gate-rele" value={formData.gate_relay || ''} onChange={(e) => setFormData({ ...formData, gate_relay: e.target.value })} required={!!formData.gate_device}>
                    <option value="">—</option>
                    <option value="1">Relé 1</option>
                    <option value="2">Relé 2</option>
                  </select>
                  <small>Qual relé da controladora fecha o contato da botoeira desta câmera. Um relé atende uma câmera só.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="cam-gate-pulso">Pulso (ms)</label>
                  <input id="cam-gate-pulso" type="number" min="200" max="10000" step="100" value={formData.gate_pulse_ms ?? 1000}
                    onChange={(e) => setFormData({ ...formData, gate_pulse_ms: parseInt(e.target.value, 10) || 1000 })} />
                  <small>Quanto tempo o contato fica fechado a cada abertura. 1000 ms é o padrão.</small>
                </div>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                <FiCheck /> Salvar
              </button>
              <button type="button" className="btn btn-danger" onClick={handleCancel}>
                <FiX /> Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="cameras-grid">
        {cameras.length === 0 ? (
          <p className="no-data">Nenhuma câmera cadastrada</p>
        ) : (
          cameras.map(camera => {
            const statusInfo = getStatusInfo(camera.status);
            const isInativa  = camera.status === 'inativa';
            const isToggling = togglingId === camera.id;
            const roi        = parseRoi(camera.roi_config);

            return (
              <div
                key={camera.id}
                className={`camera-card ${isInativa ? 'camera-card-inativa' : ''}`}
              >
                <div className="camera-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: '10px', height: '10px', borderRadius: '50%',
                      backgroundColor: statusInfo.dot,
                      display: 'inline-block',
                      boxShadow: camera.status === 'ativa' ? `0 0 6px ${statusInfo.dot}` : 'none'
                    }} />
                    <h3 style={{ opacity: isInativa ? 0.5 : 1 }}>{camera.name}</h3>
                  </div>
                  <span className={`badge badge-${camera.camera_type}`}>
                    {camera.camera_type === 'entrada' ? '📥 Entrada' : '📤 Saída'}
                  </span>
                </div>

                <div className="camera-info" style={{ opacity: isInativa ? 0.5 : 1 }}>
                  <p><strong>URL:</strong> <span style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{camera.rtsp_url}</span></p>
                  <p><strong>Intervalo:</strong> {camera.frame_interval}ms</p>
                  {camera.camera_ip_address && <p><strong>IP interno:</strong> {camera.camera_ip_address}</p>}
                  {camera.recognition_source === 'camera_lpr' && <p><strong>Reconhecimento:</strong> câmera LPR Alphadigi (pela integração)</p>}
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`status ${statusInfo.cls}`}>{statusInfo.label}</span>
                  </p>
                  {/* Badge de ROI */}
                  <p>
                    <strong>Zona de Leitura:</strong>{' '}
                    {roi ? (
                      <span className="roi-badge roi-badge-active" title={Array.isArray(roi.polygon) ? 'Zona poligonal (Onda 17); valores do retângulo envolvente' : 'Zona retangular'}>
                        <FiCrosshair size={11} />
                        {Array.isArray(roi.polygon) && <>{' '}Polígono ({roi.polygon.length} vértices) ·</>}
                        {' '}X {(roi.x1 * 100).toFixed(0)}%–{(roi.x2 * 100).toFixed(0)}%
                        {' '}| Y {(roi.y1 * 100).toFixed(0)}%–{(roi.y2 * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="roi-badge roi-badge-none">Toda a imagem</span>
                    )}
                  </p>
                  {/* Onda 179: a controladora de saída desta câmera e o teste do instalador */}
                  {camera.camera_type === 'saida' && camera.gate_device && (
                    <p className="cam-semaforo-info cam-liberacao-info">
                      <strong>Liberação de saída:</strong>{' '}
                      controladora <code>{camera.gate_device}</code> · relé {camera.gate_relay || '?'} · pulso {Number(camera.gate_pulse_ms) || 1000} ms
                      {can('cameras_manage') && (
                        <span className="cam-semaforo-teste">
                          <button type="button" className="btn btn-sm btn-success" disabled={!!testandoSemaforo}
                            onClick={() => testarLiberacao(camera, 'abrir')} title="Pulsa o relé desta câmera (um bipe longo) e espera a confirmação da placa">
                            {testandoSemaforo === `${camera.id}:abrir` ? '…' : 'Testar abertura'}
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" disabled={!!testandoSemaforo}
                            onClick={() => testarLiberacao(camera, 'negar')} title="Dois bipes curtos na placa, nenhum relé: o sinal de bloqueio">
                            {testandoSemaforo === `${camera.id}:negar` ? '…' : 'Testar bloqueio'}
                          </button>
                        </span>
                      )}
                    </p>
                  )}
                  {/* Onda 141: o semáforo desta câmera e o teste do instalador */}
                  {temSemaforo && (camera.semaforo_device || Number(camera.semaforo_enabled) === 1) && (
                    <p className="cam-semaforo-info">
                      <strong>Semáforo:</strong>{' '}
                      <span className={`cam-semaforo-badge ${Number(camera.semaforo_enabled) === 1 ? 'cam-semaforo-ligado' : 'cam-semaforo-desligado'}`}>
                        <FiZap size={11} /> {Number(camera.semaforo_enabled) === 1 ? 'Ligado' : 'Desligado'}
                      </span>
                      {camera.semaforo_device && <> · controladora <code>{camera.semaforo_device}</code></>}
                      {can('cameras_manage') && camera.semaforo_device && (
                        <span className="cam-semaforo-teste">
                          <button type="button" className="btn btn-sm btn-success" disabled={!!testandoSemaforo}
                            onClick={() => testarSemaforo(camera, 'verde')} title="Acende o verde na bancada por alguns segundos">
                            {testandoSemaforo === `${camera.id}:verde` ? '…' : 'Testar verde'}
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" disabled={!!testandoSemaforo}
                            onClick={() => testarSemaforo(camera, 'vermelho')} title="Acende o vermelho (com bipe) na bancada por alguns segundos">
                            {testandoSemaforo === `${camera.id}:vermelho` ? '…' : 'Testar vermelho'}
                          </button>
                          {/* Onda 142: o âmbar da saída na tolerância */}
                          <button type="button" className="btn btn-sm btn-warning" disabled={!!testandoSemaforo}
                            onClick={() => testarSemaforo(camera, 'ambar')} title="Acende o âmbar (um bipe curto) na bancada por alguns segundos — é a cor da saída dentro da tolerância">
                            {testandoSemaforo === `${camera.id}:ambar` ? '…' : 'Testar âmbar'}
                          </button>
                        </span>
                      )}
                    </p>
                  )}
                  {camera.last_error && (
                    <p style={{ color: '#ef4444', fontSize: '0.8rem' }}>
                      ⚠ {camera.last_error}
                    </p>
                  )}
                </div>

                {can('cameras_manage') && (
                  <div className="camera-actions">
                    {/* Ativar/Desativar */}
                    <button
                      className={`btn btn-sm ${isInativa ? 'btn-success' : 'btn-warning'}`}
                      onClick={() => handleToggle(camera)}
                      disabled={isToggling}
                      title={isInativa ? 'Ativar câmera' : 'Desativar câmera'}
                    >
                      {isToggling ? (
                        <span>Aguarde...</span>
                      ) : isInativa ? (
                        <><FiWifi /> Ativar</>
                      ) : (
                        <><FiWifiOff /> Desativar</>
                      )}
                    </button>

                    {/* Configurar Zona de Leitura (ROI) */}
                    <button
                      className={`btn btn-sm ${roi ? 'btn-roi-active' : 'btn-roi'}`}
                      onClick={() => handleOpenRoi(camera)}
                      title="Configurar zona de leitura de placas"
                    >
                      <FiCrosshair /> Zona
                    </button>

                    {/* Editar */}
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => handleEdit(camera)}
                      title="Editar câmera"
                    >
                      <FiEdit2 /> Editar
                    </button>

                    {/* Excluir — apagar câmera é da administração da plataforma */}
                    {can('cameras_roi') && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(camera.id)}
                        title="Excluir câmera permanentemente"
                      >
                        <FiTrash2 />
                      </button>
                    )}

                    {/* Onda 136: o perfil ALPR da câmera lido e gravado pela plataforma (Intelbras).
                        Fica antes do "Abrir painel", que é o último de propósito (125b). */}
                    <button
                      type="button"
                      className="btn btn-sm btn-info"
                      onClick={() => setParamCamera(camera)}
                      aria-label="Parâmetros de imagem da câmera"
                      title="Lê a configuração de imagem da própria câmera e compara com o perfil ALPR; grava só o que você marcar"
                    >
                      <FiSliders /> Parâmetros
                    </button>

                    {/* Onda 125: o painel embarcado da câmera (porta 80), repassado pela nginx do
                        servidor numa porta por câmera e alcançável só pela VPN. A URL vem do
                        servidor (painel_url); sem VPN no servidor ela é null e o botão fica desligado.
                        É o ÚLTIMO botão de propósito (125b, pedido do Iatagã): quando sobra sozinho
                        na segunda linha, é ele que se estica e ganha destaque — não o excluir. */}
                    <button
                      type="button"
                      className="btn btn-sm btn-info"
                      onClick={() => window.open(camera.painel_url, '_blank', 'noopener,noreferrer')}
                      disabled={!camera.painel_url}
                      aria-label="Abrir painel da câmera"
                      title={camera.painel_url
                        ? `Abre a interface da própria câmera em ${camera.painel_url} — exige a VPN ligada neste computador`
                        : 'O servidor não está na VPN: o painel da câmera não pode ser aberto por aqui'}
                    >
                      <FiExternalLink /> Abrir painel
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Cameras;
